import {
  ddNotifyClientInitialization,
  ddNotifyContextIntializedAction,
} from '@/utilities/logger/datadog/rumEvents/clientInitialisation';
import {
  processContextReceivedVitals,
  processGameReadyVitals,
  startMountVitalsAfterClientIframeLoad,
} from '@/utilities/logger/datadog/vitals';

import { usePopup } from '@/features/Popup/usePopup';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import { ERROR_TYPE, POPUP_TYPES } from '@/features/Popup/constants';
import { fetchRecentlyPlayed } from '@/api/recently-played/fetch-recently-played';
import { useNavigate } from 'react-router-dom';
import { getGameUrl } from '@/utilities/helpers/getRecentlyPlayedUrl';
import { syncNextGameRoute } from '@/utilities/helpers/launchNextGame';
import useSoftReload from '@/lostHooks/softReload/useSoftReload';
import { SOFT_RELOAD_PRESET } from '@/lostHooks/softReload/softReloadPresets';

import {
  setDeviceOrientationInfo,
  setGameStatus,
  setGameWindowContext,
} from '@/store/actions.ts';
import { openExternal } from '@/utilities/helpers/openExternalWebView.ts';
import { useStore } from '@/store/useStore';
import { sendGameWindowUIEnabled } from '@/game-client/helpers/sendGameWindowUIEnabled';
import { PLATFORMS } from '@/utilities/constants/game';
import {
  isRunningInsideWrapper,
  sendXsellDepositMessage,
} from '@/utilities/helpers/wrapperHelpers';
import useDepositFundsAndReloadBalance from '@/unified-modules/wallet/helpers/useAddFundsAndReloadBalance';
import { useReloadBalance } from '@/unified-modules/wallet/helpers/useReloadBalance';
import { exitGame } from '@/utilities/helpers/exitGame';
import { useShallow } from 'zustand/react/shallow';
import {
  GAME_LAUNCH_SOURCE,
  setPendingGameLaunchSource,
} from '@/utilities/logger/snowplow/game-launch-source';
import { isExperimentEnabled } from '@/utilities/logger/amplitude/experiments/isExperimentEnabled';
import {
  GAME_LAUNCHER_UM_EXPERIMENT,
  GAME_SWITCH_ENABLE_EXPERIMENT,
  USE_GAME_WINDOW_DATA_EXPERIMENT,
  getExperimentValue,
} from '@/utilities/logger/amplitude/experiments/getExperimentValue';
import { trackExperimentExposure } from '@/utilities/logger/amplitude/experiments/experimentExposure';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  markClientHandshakeReceived,
  markGlsInitializeUrlContextReceived,
} from '@/game-client/gls-pending-initialize-url';
import {
  isRealityCheckDismissedEvent,
  isRealityCheckShownEvent,
} from '@/unified-modules/rg/reality-check-events';
import {
  dequeueRealityCheckAutoplayGuard,
  queueRealityCheckAutoplayGuard,
} from '@/unified-modules/rg/reality-check-queue';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import { refreshExternalAccountSession } from '@/unified-modules/account/external-session-refresh';
import { useAccountUm } from '@/unified-modules/account/useAccountUm';
import { handleError } from '@/utilities/errors/handleError';
import { ErrorType } from '@/utilities/errors/types';
import { useGameSwitch } from '@/game-client/hooks/use-game-switch';

export const useClientCommunicationHandlers = () => {
  const { keepSessionAlive } = useAccountUm();
  const { showGameWindow } = useStore((s) => s.commonUIContext);
  const platform = useStore((s) => s.runtime.platform);
  const depositLink = useStore((s) => s.commonUIContext?.depositLink);
  const depositFundsAndReloadBalance = useDepositFundsAndReloadBalance();
  const reloadBalance = useReloadBalance();
  const { showPopup } = usePopup();
  const { setGameWindowExpansionStateDisabled } = useStore(
    useShallow((state) => state.swj),
  );
  const { provider, launchMethod, amplitudeExperiments } = useStore(
    useShallow((state) => ({
      provider: state.gameContext.provider,
      launchMethod: state.gameContext.launchMethod,
      amplitudeExperiments: state.amplitudeExperiments,
    })),
  );

  const handshakeEventHandler = () => {
    markClientHandshakeReceived();
    startMountVitalsAfterClientIframeLoad();

    ddNotifyClientInitialization();
  };

  const { gameSwitch } = useGameSwitch();
  const { reload } = useSoftReload();
  const navigate = useNavigate();

  const contextReceivedEventHandler = (event) => {
    markGlsInitializeUrlContextReceived();
    const payload = event?.data?.payload ?? {};
    ddNotifyContextIntializedAction(payload);
    processContextReceivedVitals();
  };

  const gameReadyEventHandler = () => {
    setTimeout(() => {
      setGameStatus({ gameLoadSuccess: true });
    }, 10); // small delay to allow any last progress events to be processed

    Logger.logRumAction({
      name: '[INIT] Game launch successful',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Action,
    });

    processGameReadyVitals();
  };

  const gameLoadProgressEventHandler = (event) => {
    setGameStatus({ gameLoadProgress: event.data.payload });
  };

  const spinInProgressEventHandler = (event) => {
    const spinInProgress = event.data.payload;
    const { spinEndCount = 0 } = useStore.getState().gameWindowContext;

    if (spinInProgress) {
      Logger.logRumAction({
        name: '[GW][SPIN_IN_PROGRESS] Spin in progress',
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Info,
        context: {
          spinInProgress,
        },
      });
    }
    setGameWindowContext({
      spinInProgress,
      // Bump on every spin end so consumers re-run even when spinInProgress
      // toggles back to the same value (e.g. DGC free-spins sequences).
      spinEndCount: spinInProgress ? spinEndCount : spinEndCount + 1,
    });
    setGameWindowExpansionStateDisabled(spinInProgress);
    if (!spinInProgress) {
      setTimeout(() => {
        void keepSessionAlive();
      }, 2000);
    }
  };

  const handleProviderError = (event) => {
    const { payload } = event.data;

    const gameLauncherUMExperiment =
      getExperimentValue(amplitudeExperiments, GAME_LAUNCHER_UM_EXPERIMENT) ??
      getExperimentValue(amplitudeExperiments, 'gameLauncherUMEnabled') ??
      'unknown';

    Logger.logRumError({
      name: '[GW][PROVIDER_ERROR] Provider error',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
      context: {
        provider: provider?.toUpperCase(),
        launchMethod,
        gameLauncherUMExperiment,
        ...payload,
      },
    });

    handleError({
      type: ErrorType.GAME,
      payload: {
        errorType: payload?.errorType,
        errorMessage: payload?.errorMessage,
        errorCode: payload?.errorCode,
        errorParameters: payload?.errorParameters,
      },
    });

    showPopup(
      generatePopupConfig(POPUP_TYPES.PROVIDER, {
        errorConfig: {
          errorType: payload.errorType,
          errorMessage: payload.errorMessage,
          errorCode: payload.errorCode,
          errorParameters: payload?.errorParameters,
        },
      }),
      POPUP_TYPES.PROVIDER,
    );
  };

  const toggleAudioEventHandler = (event) => {
    setGameWindowContext({
      audioEnabled: event.data.payload,
    });
  };

  const requestGameWindowUiStateHandler = () => {
    sendGameWindowUIEnabled(!!showGameWindow);
  };

  const handleShowFunds = async () => {
    if (platform === PLATFORMS.Desktop) {
      await depositFundsAndReloadBalance();
    } else if (platform === PLATFORMS.Android) {
      window.location.href = depositLink ?? '';
    }

    sendXsellDepositMessage();
  };

  const handleCloseGameLauncher = () => {
    exitGame();
  };

  const handleResetGame = () => {
    window.location.reload();
  };

  // Two gates, both required. Game switch depends on casino-game-window-data,
  // so `use-game-window-data` is the secondary check that keeps the game-switch
  // experiment from going live anywhere that service has not rolled out.
  const isGameSwitchEnabled = () => {
    const experiments = useStore.getState().amplitudeExperiments;

    trackExperimentExposure(GAME_SWITCH_ENABLE_EXPERIMENT);
    trackExperimentExposure(USE_GAME_WINDOW_DATA_EXPERIMENT);

    return (
      isExperimentEnabled(experiments, GAME_SWITCH_ENABLE_EXPERIMENT) &&
      isExperimentEnabled(experiments, USE_GAME_WINDOW_DATA_EXPERIMENT)
    );
  };

  const showFailedToRedirectPopup = () => {
    showPopup(
      generatePopupConfig(POPUP_TYPES.PROVIDER, {
        errorConfig: { errorType: ERROR_TYPE.FAILED_TO_REDIRECT },
      }),
      POPUP_TYPES.PROVIDER,
    );
  };

  // In-app launch: sync the route and soft-reload with the launch-new-game
  // preset (no document reload). Xsell still opens the game externally.
  const redirectToGame = (gameUid: string) => {
    if (useStore.getState().runtime.wrapperType.isXsell) {
      const gameUrl = getGameUrl(gameUid);
      if (gameUrl) {
        openExternal(gameUrl);
      }
      return;
    }

    setPendingGameLaunchSource(GAME_LAUNCH_SOURCE.RECENTLY_PLAYED_IN_GAME);
    syncNextGameRoute(gameUid, navigate);
    reload({ preset: SOFT_RELOAD_PRESET.LAUNCH_NEW_GAME });
  };

  const handleResumeIncompleteGame = async (event) => {
    const rgsCode = event?.data?.payload;

    // Experiment on: resolve the game through the game switch flow
    // (game-window-data) instead of recently played, then launch it.
    // `gameSwitch` reports its own failures, so no popup here when it fails.
    if (isGameSwitchEnabled()) {
      const switchedGameUid = await gameSwitch({
        rgsCode,
        provider: provider ?? undefined,
      });
      if (switchedGameUid) {
        redirectToGame(switchedGameUid);
      }
      return;
    }

    const { wrapperType } = useStore.getState().runtime;

    let recentlyPlayedGames =
      useStore.getState().recentlyPlayedContext.recentlyPlayed;

    if (!recentlyPlayedGames?.length) {
      recentlyPlayedGames = (await fetchRecentlyPlayed()) ?? null;
    }

    const redirectGameUid = recentlyPlayedGames?.find((game) =>
      wrapperType.isAndroid
        ? game?.providerRefMobile === rgsCode
        : game?.providerRefDesktop === rgsCode,
    )?.gameUid;

    if (!redirectGameUid) {
      showFailedToRedirectPopup();
      return;
    }

    redirectToGame(redirectGameUid);
  };

  const handleGameSwitch = async (event) => {
    if (!isGameSwitchEnabled()) {
      return;
    }

    const { rgsCode, provider } = event?.data?.payload ?? {};
    await gameSwitch({ rgsCode, provider });
  };

  const handleRequestGameClose = () => {
    if (isRunningInsideWrapper()) {
      exitGame();
    }
  };

  const handleDeviceOrientationInfo = (event) => {
    const { direction, safeZone } = event?.data ?? {};
    setDeviceOrientationInfo({ direction, safeZone });
  };

  const handleContextParamMissing = (event) => {
    const payload = event?.data?.payload;
    showPopup(
      generatePopupConfig(POPUP_TYPES.PROVIDER, {
        errorConfig: {
          errorType: ERROR_TYPE.GENERAL_ERROR,
          errorMessage: `The following parameters are missing from the url: ${payload}`,
        },
        customConfig: {
          props: {
            popupName: 'Invalid input provided',
          },
        },
      }),
      POPUP_TYPES.PROVIDER,
    );
  };

  const handleModularDepositDismissed = () => {
    const { runtime, userSessionContext } = useStore.getState();

    if (!runtime.wrapperType.isAndroid || !userSessionContext?.hasActiveSession)
      return;

    void reloadBalance();
  };

  const handleRealityCheckModalEvent = (event: Event) => {
    const rcEvent = (event as MessageEvent)?.data?.event;
    if (isRealityCheckShownEvent(rcEvent)) {
      queueRealityCheckAutoplayGuard();
    } else if (isRealityCheckDismissedEvent(rcEvent)) {
      dequeueRealityCheckAutoplayGuard();
    }
  };

  const handleSessionExtended = () => {
    if (isEmbeddedInLobby()) {
      return;
    }

    void refreshExternalAccountSession();
  };

  return {
    handshakeEventHandler,
    contextReceivedEventHandler,
    gameReadyEventHandler,
    gameLoadProgressEventHandler,
    spinInProgressEventHandler,
    handleProviderError,
    toggleAudioEventHandler,
    requestGameWindowUiStateHandler,
    handleShowFunds,
    handleCloseGameLauncher,
    handleResetGame,
    handleResumeIncompleteGame,
    handleGameSwitch,
    handleRequestGameClose,
    handleContextParamMissing,
    handleDeviceOrientationInfo,
    handleRealityCheckModalEvent,
    handleModularDepositDismissed,
    handleSessionExtended,
  };
};
